import { Injectable } from '@angular/core';
import { Howl, Howler } from 'howler';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { generalAudioEffects, themeAudioEffects } from '../definitions/general-audio-effects';
import { environment } from '../../../environments/environment';

import { ThemeService } from '../../shared/theme/services/theme.service';
import { AudioService } from '../services/audio.service';

@Injectable({
  providedIn: 'root',
})
export default class AudioHelper {

  private audioFiles: Map<string, Howl>;
  private subscription: Subscription;

  private audioEnabled: boolean;
  private audioVolume: number;

  private themesWithAudio: string[] = ['heroes', 'champs', 'globetrotters', 'superstars', 'investigators', 'hub'];
  private currentTheme: string;
  private audioPath: string;

  /**
   * Constructor
   *
   * @param themeService
   * @param audioService
   */
  public constructor(private themeService: ThemeService, private audioService: AudioService) {

    this.audioFiles = new Map<string, Howl>();
    this.subscription = new Subscription();

    this.subscription.add(
      this.audioService.getAudioEnabled()
        .subscribe({
          next: (enabled) => {
            this.audioEnabled = enabled;
          },
        }),
    );

    this.subscription.add(
      this.audioService.getCurrentVolume()
        .pipe(debounceTime(500))
        .subscribe({
          next: (updatedVolume) => {
            if (updatedVolume !== this.audioVolume) {
              this.setMasterVolume(updatedVolume);
              this.audioVolume = updatedVolume;
            }
          },
        }),
    );

    this.subscription.add(
      this.themeService.getThemeChange()
        .subscribe({
          next: (theme: string) => {
            if (theme !== undefined && this.themesWithAudio.includes(theme)) {
              // Unload all audio files from the previous theme
              this.audioFiles.forEach((audioInstance: Howl) => {
                audioInstance.stop().unload();
              });

              // Set the new theme
              this.currentTheme = theme;

              // Set up the audio path
              this.audioPath = (environment.production)
                ? this.themeService.assetsDir + this.currentTheme + '/shared/audio/'
                : 'assets/themes/' + this.currentTheme + '/shared/audio/';
            }
          },
        }),
    );

  }

  /**
   * Play an audio effect. Load it first when it's the first time the effect is requested.
   *
   * @param {string} effect
   *
   * @return void
   */
  public playAudio(effect: string): void {
    const existingAudio: Howl = this.fetchAudioFile(effect);
    if (existingAudio !== undefined) {
      existingAudio.stop();
      existingAudio.play();
    }
  }

  /**
   * ?
   * @param {string} effect
   *
   * @return void
   */
  public stopAudio(effect: string) {
    const existingAudio: Howl = this.audioFiles.get(effect);
    if (existingAudio !== undefined) {
      existingAudio.stop();
    }
  }

  /**
   * ?
   * @param {string} effect
   *
   * @return void
   */
  public stopLoopAudio(effect: string) {
    const existingAudio: Howl = this.audioFiles.get(effect);
    if (existingAudio !== undefined) {
      existingAudio.stop();
    }
  }

  /**
   * ?
   * @param {string} effect
   */
  public restartLoopAudio(effect: string) {
    const existingAudio: Howl = this.fetchAudioFile(effect);
    if (existingAudio !== undefined) {
      existingAudio.seek(0);
    }
  }

  /**
   * ?
   * @param {string} effect
   */
  public loopAudio(effect: string) {
    const existingAudio: Howl = this.fetchAudioFile(effect);
    if (existingAudio !== undefined) {
      existingAudio.loop(true);
      existingAudio.play();
    }
  }

  /**
   * Sets the master volume
   *
   * @param {number} level
   *
   * @return void
   */
  public setMasterVolume(level: number): void {
    Howler.volume(level / 10);
  }

  /**
   * Sets the volume
   *
   * @param effect
   * @param {number} level
   *
   * @return void
   */
  public setVolume(effect: string, level: number): void {
    const existingAudio: Howl = this.fetchAudioFile(effect);
    if (existingAudio !== undefined) {
      existingAudio.volume(level);
    }
  }

  /**
   * Fades the audio
   *
   * @param effect
   * @param from
   * @param to
   * @param time
   *
   * @return void
   */
  public fadeAudio(effect: string, from: number, to: number, time: number): void {
    const existingAudio: Howl = this.fetchAudioFile(effect);
    if (existingAudio !== undefined) {
      existingAudio.fade(from, to, time);
    }
  }

  /**
   * Pans the audio
   *
   * @param effect
   * @param position
   *
   * @return void
   */
  public panAudio(effect: string, position: number): void {
    const existingAudio: Howl = this.fetchAudioFile(effect);
    if (existingAudio !== undefined) {
      existingAudio.stereo(position);
    }
  }

  /**
   * Get the audio effect as desired, and load it if it's not loaded before.
   *
   * @param {string} effect
   *
   * @returns Howl
   */
  private fetchAudioFile(effect: string): Howl {
    // Check if the audio was already loaded
    let howlAudioInstance: Howl = this.audioFiles.get(effect);
    if (howlAudioInstance === undefined) {

      // Load the audio first from the generic audio files
      let matchingAudioFile = generalAudioEffects.get(effect);
      if (matchingAudioFile === undefined) {
          // Try to load the audio from the current theme if it's not found in the generic files
          matchingAudioFile = this.currentTheme
            ? (themeAudioEffects[this.currentTheme] as Map<string, string>).get(effect)
            : undefined;
      }

      if (matchingAudioFile) {
        const newHowlAudioInstance: Howl = new Howl({
          src: [this.audioPath + matchingAudioFile],
        });

        this.audioFiles.set(effect, newHowlAudioInstance);
        howlAudioInstance = newHowlAudioInstance;
      }
    }

    return howlAudioInstance;
  }
}
